import { create } from 'zustand';
import i18n from '@/i18n';
import { api } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';

export type SupportedLocale = 'hy' | 'ru' | 'en';

const SUPPORTED_LOCALES: SupportedLocale[] = ['hy', 'ru', 'en'];
const LOCALE_STORAGE_KEY = 'senseface.locale';

function isSupportedLocale(value: string): value is SupportedLocale {
  return (SUPPORTED_LOCALES as string[]).includes(value);
}

interface LocaleState {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale, silent?: boolean) => void;
}

export const useLocaleStore = create<LocaleState>()((set) => ({
  locale: 'en',

  setLocale: (locale, silent = false) => {
    set({ locale });
    void i18n.changeLanguage(locale);
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    document.documentElement.lang = locale;

    if (!silent && useAuthStore.getState().token) {
      api.patch('/auth/me/locale', { locale }).catch((err: unknown) => {
        console.error('[localeStore] Failed to sync locale to backend:', err);
      });
    }
  },
}));

export function initLocale(): void {
  let resolved: SupportedLocale = 'en';

  const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
  if (stored && isSupportedLocale(stored)) {
    resolved = stored;
  } else {
    const navLang = navigator.language.split('-')[0];
    if (isSupportedLocale(navLang)) {
      resolved = navLang;
    }
  }

  document.documentElement.lang = resolved;
  useLocaleStore.getState().setLocale(resolved, true);
}
